<template>
    <input-base :params="params" :inputFocused="inputFocused" :inputValue="inputValue === null ? '' : inputValue">
        <!-- Input slot -->
        <template v-slot:input>
            <input
            readonly
            ref="inputWrapper"
            v-mask="params.mask"
            :type="'text'"
            :class="{'error-state': params.errorState }"
            :value="inputContent"
            :maxlength="params.maxLength"
            :placeholder="params.placeholder"
            @click="showDatePicker = true"
            @focus="[emit('onFocus'), inputFocused = true]"
            @blur="[emit('onBlur'), inputFocused = false]"
            @keyup.enter="emit('onEnter')"
            @input="handleInputChange">
            <!-- Calendar Icon -->
            <calendar-icon
            class='calendar-icon'
            @click="showDatePicker = true"/>
            <!-- Date Picker -->
            <teleport to='#more-options-mobile-layer-overlay-teleport' :disabled="!applyPositionFixed">
                <transition name="show-datepicker" @before-enter="beforeEnter">
                    <div v-if="showDatePicker" class="calendar-wrapper-for-animation dont-close-side-modals dont-close-centered-modals">
                        <date-picker
                        class="date-picker"
                        v-click-outside="() => closePicker()"
                        :value="inputValue"
                        :openDate="inputValue"
                        :cancelButtonText="t('cancel')"
						:monthsToShow="monthsToShow"
						:disabledMonthMessage="disabledMonthMessage"
						:enabledDates="enabledDates"
                        :disabledDates="disabledDates"
                        :disabled="disabled"
                        :disableWeekends="disableWeekends"
                        @updatedDate="updateDateHandler"
                        @close="showDatePicker = false"/>
                    </div>
                </transition>
            </teleport>
        </template>
    </input-base>
</template>

<script setup>
/* Components */
import InputBase from '@/components/generic/inputs/Base'
import CalendarIcon from '@/assets/icons/calendar.svg'
import DatePicker from '@/components/generic/datepicker/DatePicker'

/* Vue, Helpers Imports */
import { ref, watch, onMounted } from 'vue'
import { getDate, createJSONDateNoTimezone } from '@/assets/scripts/dateBuilders'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'

/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['input', 'onFocus', 'onBlur'])

/* Store */
const { getMobileAppState, getFooterState } = storeToRefs(useProjectBaseStore())

/** props received
@params {
    - labelText {String} label for textField
    - darkBackgroundVariant {Boolean} property to apply specific colors for blue backgrounds
    - bottomMessage {String} text to display in bottom elements
}
@inputValue {String, Number, Date} value to be displayed in input
@disabledDates {Object} - {to: minDate, from: maxDate }
@enabledDates {Array} - Array of dates we want to enabled
@monthsToShow {Array} array of months to show
@disabledMonthMessage {String} to display next to disabled months
@errorState {Boolean} - property to show if input has error or not
@disabled {Boolean}
*/
const props = defineProps({
    params: Object,
    inputValue: [String, Number, Date],
    disabledDates: Object,
	enabledDates: Array,
	monthsToShow: Array,
	disabledMonthMessage: String,
    errorState: Boolean,
	disabled: Boolean,
    disableWeekends: Boolean
})

/* Data */
const showDatePicker = ref(false)
const inputContent = ref('')
const inputWrapper = ref('')
const inputFocused = ref(false)

/* Watcher */
watch(() => props.inputValue, () => {
    inputContent.value = props.inputValue ? getDate(props.inputValue) : ''
})

/* Methods */
const updateDateHandler = (date) => {
    if (date === null) {
        inputContent.value = null
        emit('input', inputContent.value)
        closePicker()
        return
    }

    /* Format Date to Tue Apr 05 2022 00:00:00 GMT+0100 (Western European Summer Time) */
    const formatedDate = new Date(new Date(date.timestamp).setHours(0, 0, 0, 0))
    /* Format Date to DD/MM/YYYY */
    inputContent.value = getDate(new Date(date.timestamp))
    /* Emits String: 0000-00-00T00:00:00.000Z */
    emit('input', createJSONDateNoTimezone(formatedDate))
    closePicker()
}

const closePicker = () => {
    showDatePicker.value = false
}

/*************************************************
*                                                *
*                Open/Close Logic                *
*                                                *
*************************************************/
const applyPositionFixed = ref(false)
const CALENDAR_WIDTH = 296
const CALENDAR_HEIGHT = 416 // bigest possible height
const APP_HEADER_HEIGHT_FOR_CALC = 208
const APP_NON_AUTHENTICATED_HEADER_HEIGHT_FOR_CALC = 112

const beforeEnter = (el) => {
    applyPositionFixed.value = false

    if (getMobileAppState.value) {
        el.style.position = 'fixed'
        el.style['z-index'] = validateIfParentExists(inputWrapper.value, 'overlay-modal-wrapper') ? 360 : 22
        el.style.top = '50%'
        el.style.left = `calc(50% - ${CALENDAR_WIDTH / 2}px)`
        el.style.transform = 'translate(0, -50%) scale(1)'
        applyPositionFixed.value = true

        return
    }
    /*************************************************
    *                Left/right Logic                *
    *************************************************/
    let animateMeFromLeft = false

    /* Check If inside router-content-wrapper and with menu at right */
    if (validateIfParentExists(inputWrapper.value, 'feature-content-container')) {
        if (getMobileAppState.value) {
            if (inputWrapper.value.getBoundingClientRect().right < CALENDAR_WIDTH) animateMeFromLeft = true
        } else if (validateIfParentExists(inputWrapper.value, 'right-side-wrapper')) {
            const contentParentLeft = validateIfParentExists(inputWrapper.value, 'right-side-wrapper').getBoundingClientRect().left
            if (inputWrapper.value.getBoundingClientRect().right - contentParentLeft < CALENDAR_WIDTH) animateMeFromLeft = true
        }

        /* If distanceToLeft is not enough to show calendar, we will change it's position */
        if (animateMeFromLeft) {
            el.style.left = '8px'
            el.style.right = 'unset'
        }
    }

    /*************************************************
    *                top/bottom Logic                *
    *************************************************/
	const minValueToBottom = getFooterState.value ? 92 : 8

    const distanceToBottom = window.innerHeight - (inputWrapper.value.getBoundingClientRect().top + 8) // stands for positon top

    /**
     * Here we check if the distance to the bottom is bigger than the calendar size
     * we allow the calendar to render normally below the input
     */
    if (distanceToBottom > (CALENDAR_HEIGHT + minValueToBottom)) return

    const newPosition = CALENDAR_HEIGHT - distanceToBottom

    if (validateIfParentExists(inputWrapper.value, 'feature-content-container')) {
		const distanceToTop = inputWrapper.value.getBoundingClientRect().top - (newPosition + minValueToBottom)
        const nonAuthenticatedFeature = !!validateIfParentExists(inputWrapper.value, 'non-authenticated-feature')

		/* Non-Authenticated Feature */
        if (nonAuthenticatedFeature && distanceToTop <= APP_NON_AUTHENTICATED_HEADER_HEIGHT_FOR_CALC) {
            const valueToPushUP = CALENDAR_HEIGHT - distanceToBottom > 0 ? CALENDAR_HEIGHT - distanceToBottom : 0

            el.style.position = 'fixed'
            el.style.top = `${inputWrapper.value.getBoundingClientRect().top - valueToPushUP}px`
            el.style.left = `${inputWrapper.value.getBoundingClientRect().left - 8}px`
            el.style.right = 'unset'

            applyPositionFixed.value = true
            return
        }

		/* Authenticated Feature */
        if (!nonAuthenticatedFeature && distanceToTop <= APP_HEADER_HEIGHT_FOR_CALC) {
			const valueToPushUP = CALENDAR_HEIGHT - distanceToBottom > 0 ? (CALENDAR_HEIGHT - distanceToBottom + 20) : 0

            el.style.position = 'fixed'
            el.style.top = `${inputWrapper.value.getBoundingClientRect().top - valueToPushUP}px`
            el.style.left = `${inputWrapper.value.getBoundingClientRect().left - 8}px`
            el.style.right = 'unset'

            applyPositionFixed.value = true
            return
        }
    }
	/* We changes styles on our el to make it open above inputWrapper */
	el.style.top = `-${newPosition + minValueToBottom}px`
}

onMounted(() => {
	inputContent.value = props.inputValue ? getDate(props.inputValue) : ''
})
</script>

<style lang="scss" scoped>
input {
    width: 100%;
    height: 40px;
    position: relative;

    padding-right: 16px;

    @include H6-Regular;
    line-height: 18px;
    color: var(--secondary-dark-3);
    text-transform: capitalize;
    cursor: pointer;
}

.calendar-icon {
    width: 16px;
    height: 16px;

    display: block;
    position: absolute;
    bottom: 12px; // 12px from "border"
    right: 12px;

    flex-shrink: 0;
    cursor: pointer;

    :deep(.change-color-fill) {
        fill: var(--secondary-dark-2);
    }
}

.calendar-wrapper-for-animation {
	width: 296px;

    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 100;

    opacity: 1;
    transition: opacity 0.2s linear;

    &.fixed-position {
        position: fixed;
        top: unset;
        right: unset;
    }

    &.show-datepicker-enter-from,
    &.show-datepicker-enter,
    &.show-datepicker-leave-active {
        opacity: 0;
    }

    &.show-datepicker-active,
    &.show-datepicker-leave {
        opacity: 1;
    }
}
</style>
